namespace Core {

    export interface NetValues {
        SupplierNetRate:number|string;
        SupplierCurrency?:string;
        SupplierGrossRate?:number|string;
        SupplierBindingPrice?:number|string;
        CommissionAmount:any;
        CommissionPercentage:number|string;
        CommissionCurrency?:string;
        Tax:number;
        TaxCommissionAmount:any;
        TaxCommissionPercentage:number|string;
        TotalGross:number;
        TotalGrossCurrency?:string;
        ExchangeRate?:number;
        ExchangeRateTotal?:number;
        ExchangeCurrency?:string;
        Markups?:CalculationItem[];
        Promotions?:CalculationItem[];
        PriceAdjustments?:CalculationItem[];
        CommissionType:string;
        OutputVat?:number;
        OutputVatCurrency?:string;
    }

    interface Fare {
        Amount:number;
        Currency:number;
    }

    interface FlightData {
        AdultNetFare:Fare;
        AdultTax:Fare;
        Legs:any;
    }

    interface CostTypes {
        AdultReturn:string;
        AdultTaxAirlineFees:string;
    }

    interface CalculationItem {
        Markup?:Markup;
        Change?:Money;
        Cost:Money;
        PercentChange?:number;
        ExchangeRate?:number;
        Type:number;
        CommissionPercent?:number;
        Cancelled?:boolean;
        MarkdownAmount:number;
    }

    interface Money {
        CurrencyCode:string;
        Value:number|string;
    }

    interface Markup {
        MarkupId?:number;
        SupplierId?:number;
        MarkupAmount?:number;
        MarkupPercent?:number;
        CommissionAmount?:number;
        CommissionPercent?:number;
        AdditionalInfo:string;
        Priority:number;
        EstabGroupName?:string;
        SupplierName?:string;
        DomainName?:string;
        DepartureStartDate?:string;
        DepartureEndDate?:string;
    }

    interface Commission {
        Amount:number;
        Percent:number;
        CommissionType:string;
    }

    class AgentNetInfoService {

        costTypes:CostTypes = {
            AdultReturn: 'AdultReturnTicket',
            AdultTaxAirlineFees: 'AdultTaxandAirlineFees'
        };

        commissionTypes = {
            Commission : 'Commission',
            Markup: 'Markup'
        };

        calculationItemTypes = {
            ExchangeRate: 4,
            ClientExchangeRate: 11,
            Commission: 2,
            Rounding: 12,
            Markup:7,
            SupplierNetRate: 1,
            SupplierGrossRate: 5,
            SupplierBindingPrice: 3,
            Vat: 0,
            PriceAdjustment: 15,
            Promotion: 16,
            Markdown:99
        };

        modes = {
            Hotel : 'hotel',
            Flight: 'flight',
            Markup: 'markup',
            HotelInfo: 'hotelinfo'
        };

        requiredProps = {
            hotel : ['Type'],
            flight: ['AdultNetFare.Amount'],
            markup: ['Type'],
            hotelinfo: ['AgentToolTip.SupplierNetRate',
            'AgentToolTip.CommissionPercentage',
            'AgentToolTip.TotalCommission', 'Cost']
        };

        constructor() {}

        parse(data, mode) {
            switch(mode){
                case this.modes.Hotel:
                    return this._parseHotelData(data);
                case this.modes.Flight:
                    return this._parseFlightData(data);
                case this.modes.Markup:
                    return this._parseMarkupData(data);
                case this.modes.HotelInfo:
                    return this._parseHotelInfo(data);
                default:
                    return this._getDefaultValues();

            }
        }

        hasRequiredValues(data, mode) {
            switch(mode){
                case this.modes.Hotel:
                case this.modes.Flight:
                case this.modes.Markup:
                    return this._validate(data, this.requiredProps[mode.toLowerCase()]);
                case this.modes.HotelInfo:
                    return this._validate(data, this.requiredProps[mode.toLowerCase()], true);
                default:
                    return false;

            }
        }

        private _getDefaultValues():NetValues {
            return {
                SupplierNetRate:0, CommissionAmount:0, CommissionPercentage:0, Tax:0,
                TaxCommissionAmount:0, TaxCommissionPercentage:0, TotalGross:0,
                CommissionType: this.commissionTypes.Commission
            };
        }

        private _validate(data,props,showNegativeValues = false):boolean {
            let valid = false;
            if(_.isArray(data)){
                valid = data.every(item =>{
                    return props.every(prop => {
                        return _.has(item, prop);
                    });
                });
            } else {
                valid = props.every(prop => {
                    const hasProp = _.has(data, prop);
                    if (!showNegativeValues) {
                        return hasProp && _.get(data, prop, 0) > 0;
                    }
                    return hasProp;
                });
            }
            return valid;
        }

        private _parseHotelData( data:CalculationItem[] ):NetValues {
            const netValues:NetValues = this._getDefaultValues();

            //Commission
            const commission = data.find(item => {
                return item.Type === this.calculationItemTypes.Commission;
            });

            netValues.CommissionPercentage = this._calculateValue(commission,'CommissionPercent');
            netValues.CommissionAmount = this._calculateValue(commission,'Cost.Value');
            netValues.CommissionCurrency = this._getCurrencyCode(commission);
            // Commission changes
            const commissionChanges = data.filter(item => {
                return item.Type === this.calculationItemTypes.Markup
                && item.PercentChange && item.Cancelled === false;
            });
            if(commissionChanges && commissionChanges.length > 0){
                const firstChange = commissionChanges.find(change => {
                    return change.Cost.CurrencyCode !== '';
                });
                netValues.CommissionCurrency = this._getCurrencyCode(firstChange);
            }
            const commissionChangeTotal = commissionChanges.reduce((total, value)=> {
                return total + (value.PercentChange || 0);
            }, netValues.CommissionPercentage);

            netValues.CommissionPercentage += (commissionChanges && commissionChanges.length > 0)
            ? commissionChangeTotal : 0;

            //SupplierNetRate
            const supplierPrefs = [
                this.calculationItemTypes.ExchangeRate,
                this.calculationItemTypes.SupplierNetRate,
                this.calculationItemTypes.SupplierGrossRate
            ];

            let supplierRate;
            for (var index = 0; index < supplierPrefs.length; index++) {
                var supplierPref = supplierPrefs[index];
                supplierRate = data.find(item => {
                    return item.Type === supplierPref;
                });
                if(supplierRate) {
                    break;
                }
            }
            let supplierRateValue:number = this._calculateValue(supplierRate,'Cost.Value');
            netValues.SupplierNetRate = supplierRateValue - (supplierRate && supplierRate.Type
                === this.calculationItemTypes.SupplierNetRate
                ? 0 : netValues.CommissionAmount );
            netValues.SupplierCurrency = this._getCurrencyCode(supplierRate);

            //commission adjustments
            if (netValues.CommissionPercentage > 0 && netValues.CommissionAmount === 0) {
                netValues.CommissionAmount = this._calculatePercentageAmount(
                    netValues.SupplierNetRate,
                    netValues.CommissionPercentage
                );
            };

            // Total gross ( rounded up)
            const totalGross = data.find(item => {
                return item.Type === this.calculationItemTypes.Rounding;
            });

            const totalGrossValue = this._calculateValue(totalGross, 'Cost.Value');
            netValues.TotalGross = Math.ceil(totalGrossValue);

            //client exchange rate
            const clientExchange = data.find(item => {
                return item.Type === this.calculationItemTypes.ClientExchangeRate;
            });

            const exchangeRate = this._calculateValue(clientExchange,'ExchangeRate');
            const exchangedGross = ( exchangeRate > 0 )
            ? netValues.TotalGross / exchangeRate
            : netValues.TotalGross;

            //exchanged commission adjustments
            netValues.CommissionAmount = exchangedGross - netValues.SupplierNetRate;
            if(exchangeRate>0) {
                netValues.CommissionAmount *= exchangeRate;
            };

            //remove any markdown value from commission amount
            const markdown = data.find(item => {
                return item.Type === this.calculationItemTypes.Markdown;
            });
            const markdownValue = this._calculateValue(markdown, 'MarkdownAmount');
            netValues.CommissionAmount -= markdownValue;

            netValues.CommissionPercentage = (netValues.CommissionAmount / netValues.TotalGross) * 100;
            netValues.TotalGrossCurrency = this._getCurrencyCode(totalGross);
            netValues.CommissionCurrency = netValues.TotalGrossCurrency;

            //VAT
            const outputVat = data.find(item => {
                return item.Type === this.calculationItemTypes.Vat;
            });
            netValues.OutputVat = this._calculateValue(outputVat, 'Cost.Value');
            netValues.OutputVatCurrency = netValues.TotalGrossCurrency || this._getCurrencyCode(outputVat);

            return this._formatAsDecimals(netValues);
        }

        private _parseHotelInfo(data) {
            const netValues: NetValues = this._getDefaultValues();

            const currency = data.AgentToolTip.CurrencyCode || TRPCONFIG.Domain.CurrencyCode;
            netValues.SupplierNetRate = data.AgentToolTip.SupplierNetRate || 0;
            netValues.SupplierCurrency = this._getCurrencyCode(data.AgentToolTip.SupplierNetRate, currency);
            netValues.CommissionAmount = data.AgentToolTip.TotalCommission || 0;
            netValues.CommissionPercentage = data.AgentToolTip.CommissionPercentage || 0;
            netValues.CommissionCurrency = this._getCurrencyCode(data.AgentToolTip.TotalCommission, currency);
            netValues.TotalGross = data.Cost || 0;
            netValues.TotalGrossCurrency = this._getCurrencyCode(data.Cost, currency);
            netValues.OutputVat = data.OutputVat || 0;
            netValues.OutputVatCurrency = netValues.TotalGrossCurrency ||
                this._getCurrencyCode(data.OutputVat, currency);

            return this._formatAsDecimals(netValues);
        }

        private _parseFlightData( data:FlightData ):NetValues {
            let netValues:NetValues = this._getDefaultValues();

            netValues.SupplierNetRate = this._calculateValue(data,'AdultNetFare.Amount');

            const commissionMarkup:Markup = this._getMarkup(data,this.costTypes.AdultReturn);
            const commission = this._calculateCommission(commissionMarkup, netValues.SupplierNetRate);
            netValues.CommissionAmount = commission.Amount;
            netValues.CommissionPercentage = Math.floor(commission.Percent);
            netValues.CommissionType = commission.CommissionType;

            if( netValues.CommissionType === this.commissionTypes.Commission ) {
                netValues.SupplierNetRate -= netValues.CommissionAmount;
            }

            netValues.Tax = this._calculateValue(data,'AdultNetTax.Amount');

            const taxMarkup:Markup = this._getMarkup(data,this.costTypes.AdultTaxAirlineFees);
            const taxcommission = this._calculateCommission(taxMarkup, netValues.Tax);
            netValues.TaxCommissionAmount = taxcommission.Amount;
            netValues.TaxCommissionPercentage =  Math.floor(taxcommission.Percent);

            netValues.CommissionType = taxcommission.CommissionType;
            if( netValues.CommissionType === this.commissionTypes.Commission ) {
                netValues.Tax -= netValues.TaxCommissionAmount;
            }
            netValues.TotalGross = Math.ceil(
                  netValues.SupplierNetRate
                + netValues.CommissionAmount
                + netValues.Tax
                + netValues.TaxCommissionAmount
            );

            return this._formatAsDecimals(netValues);
        }

        private _parseMarkupData( data:CalculationItem[] ):NetValues {
            let netValues:NetValues = this._getDefaultValues();

            const commission = data.find(item => {
                return item.Type === this.calculationItemTypes.Commission;
            });

            netValues.CommissionPercentage = this._calculateValue(commission,'CommissionPercent');
            netValues.CommissionAmount = this._calculateValue(commission,'Cost.Value');
            netValues.CommissionCurrency = this._getCurrencyCode(commission);

            let supplierNetRate = data.find((item:CalculationItem) =>{
                return item.Type === this.calculationItemTypes.SupplierNetRate;
            });

            netValues.SupplierNetRate = this._calculateValue(supplierNetRate,'Cost.Value');

            let supplierGrossRate = data.find((item:CalculationItem) =>{
                return item.Type === this.calculationItemTypes.SupplierGrossRate;
            });

            let supplierBindingPrice = data.find((item:CalculationItem) =>{
                return item.Type === this.calculationItemTypes.SupplierBindingPrice;
            });
            netValues.SupplierNetRate = this._calculateValue(supplierNetRate,'Cost.Value');
            netValues.SupplierGrossRate = this._calculateValue(supplierGrossRate,'Cost.Value');
            netValues.SupplierBindingPrice = this._calculateValue(supplierBindingPrice,'Cost.Value');
            const activeRate = (supplierNetRate || supplierGrossRate || supplierBindingPrice);
            netValues.SupplierCurrency = this._getCurrencyCode(activeRate);

            const exchangeRate = data.find((item:CalculationItem) => {
                return item.Type === this.calculationItemTypes.ExchangeRate;
            });

            netValues.ExchangeRate = ( exchangeRate && exchangeRate.ExchangeRate ) || 0;
            netValues.ExchangeRateTotal = this._calculateValue(exchangeRate,'Cost.Value');
            netValues.ExchangeCurrency =  this._getCurrencyCode(exchangeRate);

            netValues.Markups = data.filter((item:CalculationItem) => {
                return item.Type === this.calculationItemTypes.Markup;
            }) || [];

            netValues.Markups = netValues.Markups.map(markup => {
                if (markup.Markup){
                    if (markup.Markup.DepartureStartDate && markup.Markup.DepartureStartDate === '0001-01-01T00:00:00')
                    { markup.Markup.DepartureStartDate = ''; }
                    if (markup.Markup.DepartureEndDate && markup.Markup.DepartureEndDate === '0001-01-01T00:00:00')
                    { markup.Markup.DepartureEndDate = ''; }
                }
                return markup;
            });

            netValues.Promotions = data.filter((item:CalculationItem) => {
                return item.Type === this.calculationItemTypes.Promotion;
            });

            netValues.PriceAdjustments = data.filter((item:CalculationItem) => {
                return item.Type === this.calculationItemTypes.PriceAdjustment;
            });

            const totalGross = data.find((item:CalculationItem) => {
                return item.Type === this.calculationItemTypes.Rounding;
            });

            netValues.TotalGross = this._calculateValue(totalGross, 'Cost.Value');
            netValues.TotalGrossCurrency = this._getCurrencyCode(totalGross);

            const outputVat = data.find(item => {
                return item.Type === this.calculationItemTypes.Vat;
            });
            netValues.OutputVat = this._calculateValue(outputVat, 'Cost.Value');
            netValues.OutputVatCurrency = this._getCurrencyCode(outputVat);

            return this._formatAsDecimals(netValues);
        }

        private _calculatePercentageAmount(value, percentage) {
            return ((value)/100) * percentage;
        }

        private _getMarkup(data, costType):Markup {
            let markup:Markup = { AdditionalInfo :'', CommissionAmount: 0, CommissionPercent: 0,
            MarkupAmount: 0, MarkupPercent: 0, Priority: 0 };

            if(!data || !costType) { return markup; };

            if(data.Legs && data.Legs.length) {
                const leg = data.Legs[0];
                if(leg.MarkupsApplied && leg.MarkupsApplied.length) {
                    markup = leg.MarkupsApplied.find((item:Markup) => {
                        return new RegExp(costType,'g').test(item.AdditionalInfo);
                    });
                }
            }
            return markup;
        }

        private _calculateCommission(data:Markup, rate:number):Commission {
            let amount = 0;
            let percent = 0;
            let commissionType = this.commissionTypes.Commission;

            if(data && rate) {
                const commissionPercentage = data.CommissionPercent || 0;
                let commissionAmount = data.CommissionAmount || 0;
                const markupPercentage = data.MarkupPercent || 0;
                let markupAmount = data.MarkupAmount || 0;
                if ( markupPercentage > 0 || markupAmount > 0) {
                    commissionType = this.commissionTypes.Markup;
                    if( markupAmount === 0 && markupPercentage > 0 ) {
                        markupAmount = this._calculatePercentageAmount(rate, markupPercentage);
                    }
                    amount = markupAmount;
                    percent = markupPercentage;
                } else {
                    if( commissionAmount === 0 && commissionPercentage > 0 ) {
                        commissionAmount = this._calculatePercentageAmount(rate, commissionPercentage);
                    }
                    amount = commissionAmount;
                    percent = commissionPercentage;
                }
            }

            return { Amount : amount, Percent: percent, CommissionType : commissionType };
        }

        private _calculateValue(data ,type, defaultValue:number = 0) {
            if(data && type) {
                return _.get(data, type, defaultValue);
            }

            return defaultValue;
        }

        private _getCurrencyCode(data, defaultValue:string = TRPCONFIG.Domain.CurrencyCode) {
            if(data) {
                return _.get(data, 'Cost.CurrencyCode', defaultValue);
            }

            return defaultValue;
        }

        private _formatAsDecimals(value) {
            return _.mapValues(value, item => {
                return (!Number.isInteger(item) && item > 0 ) ? item.toFixed(2) : item;
            });
        }

        private _toNearest10(value) {
            return Math.ceil(value/10) * 10;
        }
    }

    angular
        .module('core')
        .service('agentNetInfoService', AgentNetInfoService);

}
